import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { getUser, setUser, setToken, clearAuth, authFetch, logoutUser } from '../utils/auth';

const AuthContext = createContext(null);

const STORAGE_LANG_KEY = 'freeflash_last_lang_id';

export function AuthProvider({ children }) {
    const [user, setCurrentUser] = useState(() => getUser());
    const [loading, setLoading] = useState(true);
    const [activeLangId, setActiveLangIdState] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_LANG_KEY) || '0';
        } catch {
            return '0';
        }
    });

    const setActiveLangId = useCallback((langId) => {
        if (langId !== null && langId !== undefined) {
            const stringId = String(langId);
            setActiveLangIdState(stringId);
            try {
                localStorage.setItem(STORAGE_LANG_KEY, stringId);
            } catch {}
        }
    }, []);

    // Check session validity with the backend on app load
    useEffect(() => {
        let isMounted = true;

        async function verifySession() {
            try {
                const res = await authFetch(`${API_BASE_URL}/api/user/me`);
                if (res.ok) {
                    const userData = await res.json();
                    if (isMounted) {
                        setUser(userData);
                        setCurrentUser(userData);
                    }
                } else if (res.status === 401) {
                    if (isMounted) {
                        clearAuth();
                        setCurrentUser(null);
                    }
                }
            } catch (err) {
                console.warn('Session verification error:', err);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        verifySession();

        return () => {
            isMounted = false;
        };
    }, []);

    const login = useCallback((userData, token) => {
        if (token) setToken(token);
        if (userData) {
            setUser(userData);
            setCurrentUser(userData);
        }
    }, []);

    const logout = useCallback(async () => {
        setCurrentUser(null);
        await logoutUser();
    }, []);

    const value = {
        user,
        isAuthenticated: Boolean(user),
        loading,
        activeLangId,
        setActiveLangId,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
